/**
 * AnimatedPressable — drop-in Pressable with push-in spring animation
 *
 * Wraps Pressable in a Reanimated Animated.View so every instance gets
 * its own isolated scale shared value. Supports all Pressable props
 * including the ({ pressed }) => style function pattern.
 *
 * Variants: standard | cta | destructive | cc  (see usePressAnimation)
 *
 * THE GREEN BUTTON, IN TWO THEMES (dark-mode sprint, P2c). `variant="cta"` paints
 * the ramp for one of two reasons:
 *   - the fill is the BRAND GREEN (`fill.brand`, Cedar Prime in both themes). The
 *     ramp is the brand ramp, the same in light and in dark. This is every screen
 *     that has not been moved to the theme: its white label stays readable when
 *     the room goes dark.
 *   - the caller passes `themed`. The button is the THEME's (`cta.*`): the same
 *     ramp in light; in dark, the flat thrive fill with no lit edge, under an
 *     `ink.onCta` label. A screen passes `themed` when it is moved to the theme,
 *     in the same change that moves its fill to `cta.fill` and its label to
 *     `ink.onCta`.
 * It is a flag and not a comparison with `cta.fill`, because in dark `cta.fill`
 * IS thrive, and a button that is thrive on purpose in both themes (the alert's
 * OK, the quick view's primary) must not be taken for it.
 */

import React from 'react';
import Animated from 'react-native-reanimated';
import { Pressable, PressableProps, PressableStateCallbackType, StyleSheet } from 'react-native';
import { usePressAnimation, PressVariant } from '../../hooks/usePressAnimation';
import { GLASS_EDGE } from '../../utils/glassTokens';
import { useTheme } from '../../theme';
import { CtaGradient } from './CtaGradient.native';

type AnimatedPressableProps = PressableProps & {
  variant?: PressVariant;
  /**
   * With `variant="cta"`: this is the THEME's button (`cta.fill`, an `ink.onCta`
   * label). Left out, a brand-green fill still gets the brand ramp, as before.
   */
  themed?: boolean;
};

export function AnimatedPressable({
  variant = 'standard',
  themed = false,
  disabled,
  style,
  onPressIn: callerPressIn,
  onPressOut: callerPressOut,
  children,
  ...rest
}: AnimatedPressableProps) {
  const { animatedStyle, onPressIn, onPressOut } = usePressAnimation(variant);
  const { palette } = useTheme();

  // Compose animation handlers with caller-provided handlers
  const handlePressIn = disabled ? undefined : (e: any) => {
    onPressIn();
    callerPressIn?.(e);
  };
  const handlePressOut = disabled ? undefined : (e: any) => {
    onPressOut();
    callerPressOut?.(e);
  };

  // Resolve the base (unpressed) style to extract layout props.
  // When style is a function we call it with pressed=false to get resting values.
  const baseStyle = typeof style === 'function' ? style({ pressed: false }) : style;
  const flat = (StyleSheet.flatten([baseStyle].filter(Boolean)) ?? {}) as Record<string, unknown>;

  // Lift sizing/spacing to the outer Animated.View so percentage widths and
  // flex values resolve against the correct parent container.
  const outerLayout: Record<string, unknown> = {};
  // TWO bags, and the split is load-bearing (adversarial review, 2026-08-16):
  //   innerDefaults — values the caller MAY override, composed BEFORE its style
  //   innerOverride — values that must WIN over the caller, composed after
  // RN's flattenStyle is last-wins, so putting the injected lit edge in the
  // second bag silently discarded every caller's PRESSED borderColor.
  const innerDefaults: Record<string, unknown> = {};
  const innerOverride: Record<string, unknown> = {};

  // width/height: lift to outer AND override inner to '100%' so the Pressable fills
  // the outer container (not applying the percentage a second time).
  if (flat.width !== undefined) { outerLayout.width = flat.width; innerOverride.width = '100%'; }
  if (flat.height !== undefined) { outerLayout.height = flat.height; innerOverride.height = '100%'; }
  if (flat.flex !== undefined) { outerLayout.flex = flat.flex; innerOverride.width = '100%'; innerOverride.flexBasis = 'auto'; }
  if (flat.flexGrow !== undefined) { outerLayout.flexGrow = flat.flexGrow; innerOverride.width = '100%'; innerOverride.flexBasis = 'auto'; }
  if (flat.alignSelf !== undefined) { outerLayout.alignSelf = flat.alignSelf; }
  if (flat.position !== undefined) { outerLayout.position = flat.position; }
  if (flat.top !== undefined) { outerLayout.top = flat.top; }
  if (flat.left !== undefined) { outerLayout.left = flat.left; }
  if (flat.right !== undefined) { outerLayout.right = flat.right; }
  if (flat.bottom !== undefined) { outerLayout.bottom = flat.bottom; }

  // Margins: lift to outer AND zero on inner to prevent double-application
  if (flat.margin !== undefined) { outerLayout.margin = flat.margin; innerOverride.margin = 0; }
  if (flat.marginTop !== undefined) { outerLayout.marginTop = flat.marginTop; innerOverride.marginTop = 0; }
  if (flat.marginBottom !== undefined) { outerLayout.marginBottom = flat.marginBottom; innerOverride.marginBottom = 0; }
  if (flat.marginLeft !== undefined) { outerLayout.marginLeft = flat.marginLeft; innerOverride.marginLeft = 0; }
  if (flat.marginRight !== undefined) { outerLayout.marginRight = flat.marginRight; innerOverride.marginRight = 0; }
  if (flat.marginHorizontal !== undefined) { outerLayout.marginHorizontal = flat.marginHorizontal; innerOverride.marginHorizontal = 0; }
  if (flat.marginVertical !== undefined) { outerLayout.marginVertical = flat.marginVertical; innerOverride.marginVertical = 0; }

  // Compose effective inner style — override lifted props so the Pressable
  // fills the outer container (rather than applying the percentage/margin a second time).
  // ── The CTA ramp, applied in ONE place ──────────────────────────────
  // 52 green CTA styles across ~30 files needed the dark-to-light ramp.
  // Mounting <CtaGradient /> at 40 JSX call sites was attempted and rejected:
  // only 7 of them shared a regular enough shape to transform safely, and
  // hand-wiring the rest is 33 chances to get it subtly wrong with no device
  // verification. This component already flattens the caller's style to lift
  // layout props, so it can see the fill — and paint only the green ones.
  //
  // GATED ON THE ACTUAL COLOUR, not on `variant` alone: 38 call sites pass
  // variant="cta" and not all of them are green (the timer's controls sit on
  // a dark surface). Painting a green ramp onto a white or red button would
  // be worse than the flatness this fixes.
  //
  // …or on the caller saying so (`themed`): the theme's own button, whose fill is
  // not the brand green in dark.
  const isThemedCta = variant === 'cta' && themed;
  const isGreenCta = isThemedCta || (variant === 'cta' && flat.backgroundColor === palette.fill.brand);

  // ⚠️ THE SILENT-NO-RAMP TRAP (CTA family audit, 2026-08-16).
  //
  // The colour gate above is deliberate — a ramp on a white or red button
  // would be worse than flatness. But it is also SILENT, and that is how 12
  // call sites ended up writing `variant="cta"` on a green that the gate
  // does not cover: the code READS correct, renders flat, and nobody catches
  // it in review. It only surfaces as a founder screenshot weeks later.
  //
  // So: say something. Only for GREEN-FAMILY fills — a white/red/neutral CTA
  // is an intentional non-ramp and must stay quiet, or the warning becomes
  // noise and gets ignored. Dev-only; zero production cost.
  // `typeof` guard, not a bare `__DEV__`: this component renders inside test
  // environments that do not define the RN global, and an unguarded read is a
  // ReferenceError that takes the whole button down. (Caught by
  // StrengthCompletionSummary.editMode.test.tsx — 11 red — in this sprint.)
  if (typeof __DEV__ !== 'undefined' && __DEV__ && variant === 'cta' && !isGreenCta) {
    const bg = flat.backgroundColor;
    // Only the fills that are ALWAYS wrong. `thrive` and `cedarDark` are the
    // sanctioned dark-ground CTA fills (paired with cedarInk at 9.72:1 and
    // 12.32:1), so warning about them nagged five correct call sites —
    // including the intro screen's Get Started, which this same sprint fixed.
    // A warning that fires on correct code is a warning that gets ignored.
    const unrampedGreens: unknown[] = [palette.fill.brandLight, palette.fill.thriveDeep];
    if (typeof bg === 'string' && unrampedGreens.includes(bg)) {
      // eslint-disable-next-line no-console
      console.warn(
        `[AnimatedPressable] variant="cta" with backgroundColor ${bg} renders NO ramp — ` +
        'the gate only paints cedarPrime/primary. Either use cedarPrime (light grounds) ' +
        'or drop variant="cta" and style the dark-ground CTA explicitly (thrive + cedarInk).',
      );
    }
  }

  if (isGreenCta) {
    // A REQUIREMENT: the ramp over-bleeds 24pt (Yoga insets absolute children
    // by padding), so the button MUST clip, whatever the caller says.
    innerOverride.overflow = 'hidden';
    // A DEFAULT: give the button an edge only if it brought none — and let the
    // caller paint over it, which is exactly what CTA_GRADIENT_PRESSED does on
    // press. Injecting this as an override made the pressed edge unrenderable
    // at 13 of 14 call sites; the only one that worked was the one style that
    // spreads ...GLASS_EDGE itself and therefore skipped this branch.
    // The width is the same for both buttons, so a themed one lays out identically
    // in dark, where its edge is transparent.
    if (flat.borderWidth === undefined || flat.borderWidth === 0) {
      innerDefaults.borderWidth = GLASS_EDGE.borderWidth;
      innerDefaults.borderColor = isThemedCta ? palette.cta.edge : palette.glass.litEdge;
    }
  }

  // Pressable children may be a NODE or a ({ pressed }) => node FUNCTION —
  // several CTAs use the function form to flip their label colour. Compose
  // both shapes, or the gradient silently drops the render-prop.
  const composedChildren = !isGreenCta
    ? children
    : typeof children === 'function'
      ? (state: PressableStateCallbackType) => (
          <>
            <CtaGradient themed={isThemedCta} />
            {(children as (s: PressableStateCallbackType) => React.ReactNode)(state)}
          </>
        )
      : (
          <>
            <CtaGradient themed={isThemedCta} />
            {children}
          </>
        );

  const hasDefaults = Object.keys(innerDefaults).length > 0;
  const hasOverride = Object.keys(innerOverride).length > 0;
  const innerStyle =
    hasDefaults || hasOverride
      ? typeof style === 'function'
        ? (state: { pressed: boolean }) => [innerDefaults, style(state), innerOverride]
        : [innerDefaults, style, innerOverride]
      : style;

  return (
    <Animated.View style={[outerLayout, animatedStyle]}>
      <Pressable
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
        disabled={disabled}
        style={innerStyle as PressableProps['style']}
        {...rest}
      >
        {composedChildren}
      </Pressable>
    </Animated.View>
  );
}
